import { CloseOutlined } from "@ant-design/icons";
import { Badge, Layout } from "antd";
import { isNil } from "ramda";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import { Icon } from "@/components/icon";
import { IconTypes } from "@/components/icon/icons";
import { INDEX_NAV_WHITE_LIST } from "@/libs/constants";
import { userPermissionsState } from "@/models";
import { homeActiveTabState } from "@/models/learning-record-model";
import { HomeTabKey, TabEnum } from "@/pages/index-page/constants";

import { NavList } from "../../pages/index/components/navigation/nav-list";
import { INavItem } from "../../pages/index/components/navigation/props";
import { QuickGenerateTutorial } from "../../pages/index/components/quick-generate-tutorial/quick-generate-tutorial";
import styles from "./header.module.less";
import { LanguageSwitching } from "./language-switching";
import { SearchButton } from "./search-button";
import { useUserModalStore } from "./use-store";
import { UserInfoModal } from "./user-info-modal";

const createNavIcon = (iconKey: IconTypes) => {
  return <Icon icon={iconKey} />;
};

export const Header: FC = () => {
  const { Header: AntHeader } = Layout;

  const isIframe = (window as any).$wujie?.props?.isIframe;

  const userPermissions = useRecoilValue(userPermissionsState);

  const navigate = useNavigate();

  const { isUnreadMessage } = useUserModalStore();

  const { pathname } = useLocation();

  const { t } = useTranslation();

  const navListData: INavItem[] = useMemo(
    () => [
      {
        label: t("index_page"),
        path: "/index-page",
        icon: createNavIcon("house"),
        activeIcon: createNavIcon("houseActive"),
        children: [
          {
            key: HomeTabKey.Courses,
            label: t("course"),
            path: "/index-page",
          },
          {
            key: HomeTabKey.Tutorials,
            label: t("featured"),
            path: "/index-page",
          },
        ],
      },
      {
        label: t("learning_data"),
        path: "/learning-data",
        icon: createNavIcon("learningData"),
        activeIcon: createNavIcon("learningDataActive"),
      },
      {
        label: t("my_course"),
        path: "/my-course",
        icon: createNavIcon("myCourse"),
        activeIcon: createNavIcon("myCourseActive"),
      },
      {
        label: t("my_exam"),
        path: "/my-exams",
        icon: createNavIcon("myExamination"),
        activeIcon: createNavIcon("myExaminationActive"),
      },
      {
        label: t("exam_review"),
        path: "/review",
        icon: createNavIcon("examinationReview"),
        activeIcon: createNavIcon("examinationReviewActive"),
        isHidden: userPermissions ? !userPermissions.is_grader : true,
        wrapWidth: 110,
      },
      {
        label: t("departmental_course"),
        path: "/department-course",
        icon: createNavIcon("departmentalCourse"),
        activeIcon: createNavIcon("departmentalCourseActive"),
        wrapWidth: 165,
      },
    ],
    [t, userPermissions]
  );

  const handleNavClick = (value: INavItem) => {
    navigate(value.path);
  };

  const [activeTab, setActiveTab] = useRecoilState(homeActiveTabState);

  const activeDropdownKey =
    activeTab === TabEnum.Course ? HomeTabKey.Courses : HomeTabKey.Tutorials;

  const handleDropdownItemClick = (item: {
    key: string;
    label: string;
    path: string;
  }) => {
    const tab =
      item.key === HomeTabKey.Courses ? TabEnum.Course : TabEnum.Tutorial;

    setActiveTab(tab);
    navigate(item.path);
  };

  return (
    <AntHeader className={styles.header}>
      {isNil(isIframe) || (
        <CloseOutlined
          className="left-6 absolute z-50 top-0 bottom-0 text-lg"
          onClick={() => (window as any).$wujie?.bus?.$emit("daokey_close")}
        />
      )}

      <div
        className={`${styles.contentWrapper} ${
          pathname === "/login" ? styles.login : ""
        }`}
      >
        {/* logo */}
        <h1
          className={styles.logo}
          title="DAOKEY"
          onClick={() => navigate("/")}
        />
        {/* 导航栏 */}
        {INDEX_NAV_WHITE_LIST.includes(pathname) && (
          <div className={styles.navigationWrapper}>
            <NavList
              navList={navListData}
              activeKey={pathname}
              onNavClick={handleNavClick}
              onDropdownItemClick={handleDropdownItemClick}
              activeDropdownKey={activeDropdownKey}
            />
          </div>
        )}
        {/* 右侧内容区域 */}
        <div className={styles.rightContent}>
          {/* 搜索 */}
          {pathname === "/index-page" && <SearchButton />}
          {/* 快速生成教程 */}
          <div className={styles["margin-right-24"]}>
            {INDEX_NAV_WHITE_LIST.includes(pathname) &&
              userPermissions?.is_tutor_user && <QuickGenerateTutorial />}
          </div>
          {/* 语言切换 */}
          <LanguageSwitching />

          {/* 通知中心 */}
          <div className={styles["margin-right-24"]}>
            <Badge dot={isUnreadMessage} offset={[-3, 0]}>
              <Icon icon="bell" onClick={() => navigate("/notice-center")} />
            </Badge>
          </div>
          <UserInfoModal />
        </div>
      </div>
    </AntHeader>
  );
};
